/**
 * Placement for the endpoints overlay's satellite nodes (end-user devices
 * rendered around the switch/AP they're attached to).
 *
 * These nodes are deliberately NOT part of the fcose auto-layout subgraph
 * (see `runAutoLayout` in Views.tsx, which filters them out) or of the
 * cached-position system (see `useGraphPositions.savePositions`, which
 * filters extracted positions down to topology device ids). Instead they're
 * always positioned procedurally, fanned out on a circle around their
 * parent device's CURRENT position — so they track the parent wherever it
 * gets dragged/laid out, rather than needing their own persisted coordinates.
 */

import type { Core, NodeSingular } from 'cytoscape';

/** Radius (px) for up to this many satellites before it starts growing. */
const BASE_RADIUS = 100;
/** Extra radius (px) per satellite beyond `FREE_SATELLITES`. */
const RADIUS_PER_EXTRA = 4;
/** Hard cap (px) so a device with hundreds of endpoints doesn't sprawl. */
const RADIUS_CAP = 170;
/** Satellites up to this count share the base radius. */
const FREE_SATELLITES = 8;

function computeRadius(count: number): number {
  const radius = BASE_RADIUS + Math.max(0, count - FREE_SATELLITES) * RADIUS_PER_EXTRA;
  return Math.min(radius, RADIUS_CAP);
}

/**
 * Position every endpoint/overflow satellite node on a circle around its
 * parent device's current position.
 *
 * @param cy - The Cytoscape core instance.
 * @param parentId - When given, only that device's satellites are
 *   repositioned (used by the drag/layout handlers in Views.tsx so moving
 *   one device doesn't recompute every other device's satellites too).
 *   Omit to reposition satellites for every device (initial placement,
 *   full layout settle).
 */
export function positionEndpointSatellites(cy: Core, parentId?: string): void {
  const satellites = cy.nodes('[isEndpoint], [isEndpointOverflow]');

  const groups = new Map<string, NodeSingular[]>();
  satellites.forEach((node) => {
    const parentDeviceId = node.data('parentDeviceId') as string | undefined;
    if (!parentDeviceId) return;
    if (parentId && parentDeviceId !== parentId) return;

    let group = groups.get(parentDeviceId);
    if (!group) {
      group = [];
      groups.set(parentDeviceId, group);
    }
    group.push(node);
  });

  groups.forEach((nodes, deviceId) => {
    const parent = cy.getElementById(deviceId);
    if (parent.empty()) return;
    // `.position()` returns cytoscape's live internal position object —
    // copy the coordinates now so subsequent `node.position(...)` calls
    // below (which mutate in place) can't retroactively change what we
    // read here.
    const parentPos = parent.position();
    const originX = parentPos.x;
    const originY = parentPos.y;

    // Deterministic order (sort by node id) so the same set of satellites
    // always lands in the same arrangement across re-renders.
    const sorted = nodes.slice().sort((a, b) => a.id().localeCompare(b.id()));
    const count = sorted.length;
    const radius = computeRadius(count);

    sorted.forEach((node, index) => {
      const angle = (-90 * Math.PI) / 180 + (index / count) * 2 * Math.PI;
      const x = originX + radius * Math.cos(angle);
      const y = originY + radius * Math.sin(angle);
      node.position({ x, y });
    });
  });
}
